import { useSearchParams, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiService } from '../../lib/api';
import PostCard from './PostCard';
import CreatePost from './CreatePost';
import { themeClasses } from '../../lib/theme';
import { useAuth } from '../../hooks/useAuth';
import { useState, useMemo, useEffect } from 'react';
import { matchesJointType } from '../../utils/jointFilters';

const JOINT_TYPES = ['Hip', 'Knee', 'Shoulder', 'Foot & Ankle'] as const;

export default function SearchResults() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const query = searchParams.get('q') || '';
  const [selectedJointType, setSelectedJointType] = useState<string | null>(null);

  const { data: allPosts = [], isLoading, error } = useQuery({
    queryKey: ['searchResults', query],
    queryFn: () => query ? apiService.searchPosts(query) : Promise.resolve([]),
    enabled: !!query,
    staleTime: 30 * 1000, // Cache for 30 seconds
  });

  // Filter posts by selected joint type (searches in title, description, and tags)
  const posts = useMemo(() => {
    if (!selectedJointType) return allPosts;
    return allPosts.filter(post => matchesJointType(post, selectedJointType));
  }, [allPosts, selectedJointType]);

  // Scroll to post when posts are loaded and there's a hash in the URL
  useEffect(() => {
    if (!isLoading && posts.length > 0 && location.hash) {
      const postId = location.hash.replace('#post-', '');
      const element = document.getElementById(`post-${postId}`);

      if (element) {
        // Use requestAnimationFrame to ensure DOM is fully rendered
        requestAnimationFrame(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });

          // Highlight the post
          element.classList.add('ring-2', 'ring-healthy-green', 'ring-offset-2', 'ring-offset-white', 'dark:ring-offset-gray-900');

          // Remove highlight after 3 seconds
          setTimeout(() => {
            element.classList.remove('ring-2', 'ring-healthy-green', 'ring-offset-2', 'ring-offset-white', 'dark:ring-offset-gray-900');
          }, 3000);
        });
      }
    }
  }, [isLoading, posts, location.hash]);

  if (!query) {
    return (
      <div className="space-y-4">
        {user?.userType === 'surgeon' && <CreatePost />}
        <div className="theme-card shadow-sm p-8 text-center">
          <div className={`w-16 h-16 ${themeClasses.bgSecondary} rounded-full flex items-center justify-center mx-auto mb-4`}>
            <svg className={`w-8 h-8 ${themeClasses.textMuted}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <h3 className={`text-lg font-medium ${themeClasses.textPrimary} mb-2`}>No search query</h3>
          <p className={`${themeClasses.textSecondary} text-sm`}>Use the search box to find posts, people, and tags</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        {user?.userType === 'surgeon' && <CreatePost />}
        
        {/* Search indicator */}
        <div className={`theme-card shadow-sm p-4 border-l-4 border-l-blue-500 ${themeClasses.bgSecondary}`}>
          <div className="flex items-center space-x-3">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
            <div>
              <p className={`text-sm font-medium ${themeClasses.textPrimary}`}>
                Searching for: "{query}"
              </p>
              <p className={`text-xs ${themeClasses.textMuted}`}>
                Loading search results...
              </p>
            </div>
          </div>
        </div>

        {/* Loading skeleton */}
        <div className="theme-card shadow-sm p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-2 text-gray-600 dark:text-gray-400">Searching posts...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        {user?.userType === 'surgeon' && <CreatePost />}
        
        {/* Search indicator */}
        <div className={`theme-card shadow-sm p-4 border-l-4 border-l-red-500 ${themeClasses.bgSecondary}`}>
          <div className="flex items-center space-x-3">
            <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <p className={`text-sm font-medium ${themeClasses.textPrimary}`}>
                Search failed for: "{query}"
              </p>
              <p className={`text-xs ${themeClasses.textMuted}`}>
                {error.message}
              </p>
            </div>
          </div>
        </div>

        <div className="theme-card shadow-sm p-8 text-center">
          <div className="text-red-500 mb-2">
            <svg className="h-8 w-8 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <p className="text-gray-600 dark:text-gray-400 mb-4">Failed to search posts</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">{error.message}</p>
        </div>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="space-y-4">
        {user?.userType === 'surgeon' && <CreatePost />}

        {/* Search indicator with filter options */}
        <div className={`theme-card shadow-sm p-4 border-l-4 border-l-blue-500 ${themeClasses.bgSecondary}`}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <div>
                <p className={`text-sm font-medium ${themeClasses.textPrimary}`}>
                  Search results for: "{query}"
                </p>
                <p className={`text-xs ${themeClasses.textMuted}`}>
                  {selectedJointType ? `No ${selectedJointType.toLowerCase()} posts found` : 'No posts found'}
                </p>
              </div>
            </div>
            <button
              onClick={() => window.history.back()}
              className={`text-sm ${themeClasses.textMuted} hover:${themeClasses.textPrimary} transition-colors`}
            >
              ← Back to feed
            </button>
          </div>

          {/* Joint type filter - always show */}
          <div className="flex items-center space-x-2 pt-3 border-t border-gray-200 dark:border-gray-700">
            <span className={`text-sm ${themeClasses.textSecondary}`}>Filter by joint:</span>
            <button
              onClick={() => setSelectedJointType(null)}
              className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                selectedJointType === null
                  ? 'bg-trust-turquoise text-white'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
              }`}
            >
              All ({allPosts.length})
            </button>
            {JOINT_TYPES.map((jointType) => {
              const count = allPosts.filter(post => matchesJointType(post, jointType)).length;
              return (
                <button
                  key={jointType}
                  onClick={() => setSelectedJointType(jointType)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                    selectedJointType === jointType
                      ? 'bg-trust-turquoise text-white'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                  }`}
                >
                  {jointType} ({count})
                </button>
              );
            })}
          </div>
        </div>

        <div className="theme-card shadow-sm p-8 text-center">
          <div className={`w-16 h-16 ${themeClasses.bgSecondary} rounded-full flex items-center justify-center mx-auto mb-4`}>
            <svg className={`w-8 h-8 ${themeClasses.textMuted}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <h3 className={`text-lg font-medium ${themeClasses.textPrimary} mb-2`}>No results found</h3>
          <p className={`${themeClasses.textSecondary} text-sm mb-4`}>
            {selectedJointType
              ? `No ${selectedJointType.toLowerCase()} posts match your search. Try a different filter or adjust your search terms.`
              : 'Try adjusting your search terms or search for something else'
            }
          </p>
          {selectedJointType && allPosts.length > 0 && (
            <button
              onClick={() => setSelectedJointType(null)}
              className="px-4 py-2 mb-2 text-sm bg-trust-turquoise text-white rounded-md hover:bg-trust-turquoise/90 transition-colors"
            >
              View All Results ({allPosts.length})
            </button>
          )}
          <div className="space-x-2">
            <button
              onClick={() => window.history.back()}
              className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              Back to Feed
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {user?.userType === 'surgeon' && <CreatePost />}

      {/* Search results header */}
      <div className={`theme-card shadow-sm p-4 border-l-4 border-l-green-500 ${themeClasses.bgSecondary}`}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <div>
              <p className={`text-sm font-medium ${themeClasses.textPrimary}`}>
                Search results for: "{query}"
              </p>
              <p className={`text-xs ${themeClasses.textMuted}`}>
                Found {posts.length} post{posts.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
          <button
            onClick={() => window.history.back()}
            className={`text-sm ${themeClasses.textMuted} hover:${themeClasses.textPrimary} transition-colors`}
          >
            ← Back to feed
          </button>
        </div>

        {/* Joint type filter */}
        <div className="flex items-center space-x-2 pt-3 border-t border-gray-200 dark:border-gray-700">
          <span className={`text-sm ${themeClasses.textSecondary}`}>Filter by joint:</span>
          <button
            onClick={() => setSelectedJointType(null)}
            className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
              selectedJointType === null
                ? 'bg-trust-turquoise text-white'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
            }`}
          >
            All
          </button>
          {JOINT_TYPES.map((jointType) => (
            <button
              key={jointType}
              onClick={() => setSelectedJointType(jointType)}
              className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                selectedJointType === jointType
                  ? 'bg-trust-turquoise text-white'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
              }`}
            >
              {jointType}
            </button>
          ))}
        </div>
      </div>

      {/* Search results */}
      {posts.map((post) => (
        <PostCard key={post.id} post={post} isSearchResult={true} searchQuery={query} />
      ))}
      
      {posts.length > 0 && (
        <div className="text-center text-gray-500 dark:text-gray-400 py-8">
          <p className="text-sm">End of search results for "{query}"</p>
        </div>
      )}
    </div>
  );
}
